import React, { Suspense } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import styled from 'styled-components'
import GoogleAnalyticsReporter from '../components/analytics/GoogleAnalyticsReporter'
import AddressClaimModal from '../components/claim/AddressClaimModal'
import Header from '../components/Header'
import Polling from '../components/Header/Polling'
import { AbstractConnector } from '@web3-react/abstract-connector'
import Popups from '../components/Popups'
import { ApplicationModal } from '../state/application/actions'
import { useModalOpen, useToggleModal } from '../state/application/hooks'
import DarkModeQueryParamReader from '../theme/DarkModeQueryParamReader'
import { RedirectDuplicateTokenIds } from './AddLiquidity/redirects'

import MigrateV2 from './MigrateV2'
import MigrateV2Pair from './MigrateV2/MigrateV2Pair'
import Pool from './Pool'
import PoolFinder from './PoolFinder'
import RemoveLiquidity from './RemoveLiquidity'
import RemoveLiquidityV3 from './RemoveLiquidity/V3'
import Swap from './Swap'
import { RedirectPathToSwapOnly, RedirectToSwap } from './Swap/redirects'

import { RedirectDuplicateTokenIdsV2 } from './AddLiquidityV2/redirects'
import { PositionPage } from './Pool/PositionPage'
import AddLiquidity from './AddLiquidity'
import { ThemedBackground } from '../theme'
import FarmList from './FarmList'
import FarmPool from './FarmPool'
import RedeemXHSPX from './RedeemXHSPX'
import StakeList from './StakeList'
import Footer from 'components/Footer'
import RootWrapper from './utils/RootWrapper'
import Stake from './Stake'
import LiquidityHub from './LiquidityHub'
import Testnet from './Testnet'
import ExplorePools from './ExplorePools'
import Launcher from './Launcher'
import { SUPPORTED_WALLETS } from '../constants'
import { useActiveWeb3React } from 'hooks'
import DisclaimerModal from 'components/DisclaimerModal'
import { MAINTENANCE_IS_ACTIVATED } from 'constants/config'
import MaintenancePage from './MaintenancePage'
import DiscordModal from 'components/DiscordModal'
import VC from './VC'
import { isLauncherSubdomain, isVCSubdomain } from 'utils/data'
import ErrorPage from './ErrorPage'
import * as Sentry from '@sentry/react'
import PointsProgram from './PointsProgram'
import useScrollToTop from 'hooks/useScrollToTop'
import ReferralHandler from './utils/ReferralHandler'
import AddyHandler from './utils/AddyHandler'

const AppWrapper = styled.div`
  display: flex;
  flex-flow: column;
  align-items: flex-start;
`

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 120px;
  align-items: center;
  flex: 1;
  z-index: 1;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    padding: 16px;
    padding-top: 6rem;
  `};
`

const HeaderWrapper = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  justify-content: space-between;
  position: fixed;
  top: 0;
  z-index: 2;
`

const Marginer = styled.div`
  margin-top: 5rem;
`

function TopLevelModals() {
  const open = useModalOpen(ApplicationModal.ADDRESS_CLAIM)
  const toggle = useToggleModal(ApplicationModal.ADDRESS_CLAIM)
  return <AddressClaimModal isOpen={open} onDismiss={toggle} />
}

export default function App() {
  const { activate } = useActiveWeb3React()
  useScrollToTop()
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)

    const isDev = urlParams.get('IS_ADMIN') === 'true'

    if (isDev) {
      localStorage.setItem('SKIP_MAINTENANCE_STATUS', 'true')
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [])

  const skipMaintenance = localStorage.getItem('SKIP_MAINTENANCE_STATUS') === 'true'

  React.useEffect(() => {
    const localStorageAppVersion = localStorage.getItem('APP_VERSION')
    const currentAppVersion = process.env.REACT_APP_VERSION

    if (localStorageAppVersion !== currentAppVersion) {
      /* localStorage.clear() */
      // Remove clear on version changed
    }
    if (currentAppVersion) {
      localStorage.setItem('APP_VERSION', currentAppVersion)
    }
  }, [])

  React.useEffect(() => {
    const activateWallet = async () => {
      const lastConnector = localStorage.getItem('connector')
      if (lastConnector && SUPPORTED_WALLETS[lastConnector]?.connector && activate) {
        try {
          const provider = await SUPPORTED_WALLETS[lastConnector].connector?.getProvider()
          // Wallet has been disabled from browser since last session
          if (provider) {
            await activate(SUPPORTED_WALLETS[lastConnector].connector as AbstractConnector, undefined, true)
          }
        } catch (error) {
          console.error('Failed to activate wallet:', error)
        }
      }
    }

    activateWallet()
    // eslint-disable-next-line
  }, [])
  if (MAINTENANCE_IS_ACTIVATED && !skipMaintenance) {
    return <MaintenancePage />
  }

  return (
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      <Suspense fallback={null}>
        <Route component={GoogleAnalyticsReporter} />
        <Route component={DarkModeQueryParamReader} />
        <RootWrapper>
          <ReferralHandler />
          <AddyHandler />
          <DisclaimerModal />

          <DiscordModal />

          <AppWrapper>
            <HeaderWrapper>
              <Header />
            </HeaderWrapper>
            <BodyWrapper>
              <ThemedBackground />
              <Popups />
              <Polling />
              <TopLevelModals />
              {/*   <Web3ReactManager> */}
              <Switch>
                <Route exact strict path="/send" component={RedirectPathToSwapOnly} />
                <Route exact strict path="/swap/:outputCurrency" component={RedirectToSwap} />
                <Route exact strict path="/swap" component={Swap} />

                <Route exact strict path="/find" component={PoolFinder} />
                <Route exact strict path="/pool/v2" component={Pool} />
                <Route exact strict path="/pool" component={Pool} />
                <Route exact strict path="/pool/:tokenId" component={PositionPage} />

                <Route exact strict path="/farm" component={FarmList} />
                <Route exact strict path="/farm/:lptoken" component={FarmPool} />

                <Route exact strict path="/stake" component={StakeList} />
                <Route exact strict path="/stake/:id" component={Stake} />

                <Route exact strict path="/redeem" component={RedeemXHSPX} />

                <Route exact strict path="/liquidity-hub" component={LiquidityHub} />
                <Route exact strict path="/explore/explore-pools" component={ExplorePools} />
                <Route exact strict path="/testnet" component={Testnet} />
                <Route exact strict path="/points" component={PointsProgram} />

                <Route
                  exact
                  strict
                  path="/add/v2/:currencyIdA?/:currencyIdB?"
                  component={RedirectDuplicateTokenIdsV2}
                />

                <Route
                  exact
                  strict
                  path="/add/:currencyIdA?/:currencyIdB?/:feeAmount?"
                  component={RedirectDuplicateTokenIds}
                />

                <Route
                  exact
                  strict
                  path="/increase/:currencyIdA?/:currencyIdB?/:feeAmount?/:tokenId?"
                  component={AddLiquidity}
                />

                <Route exact strict path="/remove/v2/:currencyIdA/:currencyIdB" component={RemoveLiquidity} />
                <Route exact strict path="/remove/:tokenId" component={RemoveLiquidityV3} />

                <Route exact strict path="/migrate/v2" component={MigrateV2} />
                <Route exact strict path="/migrate/v2/:address" component={MigrateV2Pair} />

                <Route exact strict path="/vc-dashboard" component={VC} />
                <Route exact strict path="/launcher" component={Launcher} />
                {isLauncherSubdomain() && <Redirect to="/launcher" />}
                {isVCSubdomain() && <Redirect to="/vc-dashboard" />}
                <Route component={RedirectPathToSwapOnly} />
              </Switch>
              {/*   </Web3ReactManager> */}
              <Marginer />
            </BodyWrapper>
          </AppWrapper>
        </RootWrapper>
        <Footer />
      </Suspense>
    </Sentry.ErrorBoundary>
  )
}
